import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import { testNotifications } from "../../services/notification";
import {
  USER_ACCESS_HINT,
  USER_ACCESS_SUBCARD_TITLE,
} from "./userAccessFormUi";

const getSessionUserId = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null")?._id;
  } catch {
    return null;
  }
};

const resolvePushTone = (push) => {
  if (push?.sent) return "success";
  if (push?.reason === "no_token" || push?.reason === "unregistered") {
    return "warning";
  }
  return "danger";
};

const resolvePushStatusKey = (push) => {
  if (push?.sent) return "notificationTest.statusSent";
  if (push?.reason === "no_token" || push?.reason === "unregistered") {
    return "notificationTest.statusUnregistered";
  }
  return "notificationTest.statusFailed";
};

const ChannelStatusRow = ({
  channelLabel,
  statusLabel,
  tone,
  iconClass,
  details,
  expanded,
  onToggle,
  expandLabel,
}) => {
  const canExpand = Boolean(details);
  const rowClassName = [
    "notif-test-result__row",
    `notif-test-result__row--${tone}`,
    canExpand ? "notif-test-result__row--expandable" : "",
    expanded ? "is-expanded" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="notif-test-result__item">
      {canExpand ? (
        <button
          type="button"
          className={rowClassName}
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={expandLabel}
        >
          <span className="notif-test-result__icon" aria-hidden="true">
            <i className={iconClass} />
          </span>
          <div className="notif-test-result__copy">
            <span className="notif-test-result__channel">{channelLabel}</span>
            <span className="notif-test-result__status">{statusLabel}</span>
          </div>
          <span className="notif-test-result__chevron" aria-hidden="true">
            <i className="fas fa-chevron-down" />
          </span>
        </button>
      ) : (
        <div className={rowClassName}>
          <span className="notif-test-result__icon" aria-hidden="true">
            <i className={iconClass} />
          </span>
          <div className="notif-test-result__copy">
            <span className="notif-test-result__channel">{channelLabel}</span>
            <span className="notif-test-result__status">{statusLabel}</span>
          </div>
        </div>
      )}
      {canExpand && expanded ? (
        <p className="notif-test-result__details">{details}</p>
      ) : null}
    </div>
  );
};

const NotificationTestPanel = ({ userId, variant = "web", className = "" }) => {
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [expandedChannel, setExpandedChannel] = useState(null);

  const handleSendTest = async () => {
    if (sending) return;
    setSending(true);
    setLastResult(null);
    setExpandedChannel(null);

    try {
      const payload = userId ? { userId } : {};
      const sessionUserId = getSessionUserId();
      const isSelf =
        !userId || (sessionUserId && String(userId) === String(sessionUserId));
      const fcmToken = Cookies.get("fcmToken");
      if (isSelf && fcmToken) {
        payload.fcmToken = fcmToken;
      }

      const { data } = await testNotifications(payload);
      setLastResult(data);

      if (data?.push?.sent && data?.email?.sent) {
        toast.success(t("notificationTest.successBoth"));
      } else if (data?.push?.sent) {
        toast.success(t("notificationTest.successPush"));
      } else if (data?.email?.sent) {
        toast.success(t("notificationTest.successEmail"));
      } else {
        toast.warn(t("notificationTest.partialFailure"));
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 429) {
        toast.error(t("notificationTest.rateLimited"));
      } else if (status === 403) {
        toast.error(t("notificationTest.forbidden"));
      } else {
        toast.error(
          error?.response?.data?.error || t("notificationTest.sendFailed"),
        );
      }
    } finally {
      setSending(false);
    }
  };

  const toggleChannel = (channel) => {
    setExpandedChannel((current) => (current === channel ? null : channel));
  };

  const isMobile = variant === "mobile";
  const pushTone = resolvePushTone(lastResult?.push);
  const emailTone = lastResult?.email?.sent ? "success" : "danger";
  const showUnregisteredHint =
    lastResult?.push?.reason === "no_token" ||
    lastResult?.push?.reason === "unregistered";

  return (
    <div
      className={
        className ||
        (isMobile
          ? "mobile-settings-notif-test"
          : "user-access-notif-test mt-6 pt-5 border-t border-[#d3d3d3]")
      }
    >
      <h4
        className={
          isMobile
            ? "mobile-settings-notif-test-title"
            : USER_ACCESS_SUBCARD_TITLE
        }
      >
        {t("notificationTest.title")}
      </h4>
      <p
        className={
          isMobile
            ? "mobile-settings-notif-test-help"
            : `${USER_ACCESS_HINT} mb-4`
        }
      >
        {!isMobile ? (
          <i
            className="fas fa-info-circle text-[#828899] mt-0.5 shrink-0"
            aria-hidden="true"
          />
        ) : null}
        <span>{t("notificationTest.help")}</span>
      </p>

      <button
        type="button"
        onClick={() => void handleSendTest()}
        disabled={sending}
        className={
          isMobile
            ? "mobile-settings-notif-test-button"
            : "modal-button modal-button-save"
        }
      >
        {sending ? t("notificationTest.sending") : t("notificationTest.send")}
      </button>

      {lastResult ? (
        <div
          className={`notif-test-result${isMobile ? " notif-test-result--mobile" : ""}`}
          role="status"
        >
          <ChannelStatusRow
            channelLabel={t("notificationTest.channelPush")}
            statusLabel={t(resolvePushStatusKey(lastResult.push))}
            tone={pushTone}
            iconClass={
              pushTone === "success"
                ? "fas fa-check"
                : pushTone === "warning"
                  ? "fas fa-exclamation"
                  : "fas fa-times"
            }
            details={
              lastResult.push?.sent ? t("notificationTest.pushDetails") : null
            }
            expanded={expandedChannel === "push"}
            onToggle={() => toggleChannel("push")}
            expandLabel={t("notificationTest.expandPushDetails")}
          />
          <ChannelStatusRow
            channelLabel={t("notificationTest.channelEmail")}
            statusLabel={t(
              lastResult.email?.sent
                ? "notificationTest.statusSent"
                : "notificationTest.statusFailed",
            )}
            tone={emailTone}
            iconClass={
              emailTone === "success" ? "fas fa-check" : "fas fa-times"
            }
            details={
              lastResult.email?.sent ? t("notificationTest.emailDetails") : null
            }
            expanded={expandedChannel === "email"}
            onToggle={() => toggleChannel("email")}
            expandLabel={t("notificationTest.expandEmailDetails")}
          />
          {showUnregisteredHint ? (
            <p className="notif-test-result__hint">
              <i className="fas fa-info-circle" aria-hidden="true" />
              <span>{t("notificationTest.unregisteredHint")}</span>
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default NotificationTestPanel;
